import React, { useEffect, useRef, useState } from "react";
import { Modal, Spin } from "antd";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import {
  clampCropPosition,
  cropImageToFile,
  getContainScale,
  getCoverAlignedCropViewportState,
  getCropAreaFromViewport,
  getCropViewportDimensions,
  getMaxZoomForImage,
  IMAGE_CROP_BASE_MAX_ZOOM,
  IMAGE_CROP_MIN_ZOOM,
  type CropViewportState,
  type ImageCropPreset,
} from "../../Utils/imageCrop/cropImageFile";
import "./ImageCropperModal.css";

interface ImageCropperModalProps {
  open: boolean;
  imageSrc: string | null;
  preset: ImageCropPreset;
  initialCropState?: CropViewportState | null;
  /** Upload / persist in progress — Save stays loading; cancel blocked. */
  saving?: boolean;
  /** Fetching original / preparing cropper — show spinner; cancel still allowed. */
  loading?: boolean;
  title?: string;
  shape?: "rect" | "circle";
  onCancel: () => void;
  /** May be async — modal stays on Save loader until the promise settles. */
  onSave: (
    file: File,
    cropState: CropViewportState
  ) => void | Promise<void>;
  onReplace: () => void;
  disableGetContainer?:  boolean;
  viewportStyle?: React.CSSProperties;
}

const ImageCropperModal: React.FC<ImageCropperModalProps> = ({
  open,
  imageSrc,
  preset,
  initialCropState = null,
  saving = false,
  loading = false,
  title = "Position image",
  shape = "rect",
  onCancel,
  onSave,
  onReplace,
  disableGetContainer = false,
  viewportStyle,
}) => {
  const { viewportWidth, viewportHeight } = getCropViewportDimensions(
    preset.aspectRatio,
    {
      width: preset.viewportWidth,
      height: preset.viewportHeight,
    }
  );
  const fitScale =
    typeof window === "undefined"
      ? 1
      : Math.min(
          1,
          (window.innerWidth - 80) / viewportWidth,
          (window.innerHeight - 260) / viewportHeight
        );
  // Hug the crop frame. Floor keeps zoom/footer usable for small frames (143×250).
  const modalWidth = Math.max(360, Math.round(viewportWidth * fitScale) + 48);
  const dragStateRef = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(IMAGE_CROP_MIN_ZOOM);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isSaving, setIsSaving] = useState(false);

  const baseScale =
    naturalSize.width && naturalSize.height
      ? getContainScale(
          naturalSize.width,
          naturalSize.height,
          viewportWidth,
          viewportHeight
        )
      : 1;

  const maxZoom = naturalSize.width
    ? getMaxZoomForImage(
        naturalSize.width,
        naturalSize.height,
        viewportWidth,
        viewportHeight
      )
    : IMAGE_CROP_BASE_MAX_ZOOM;

  const effectiveScale = baseScale * zoom;

  const centerWhenContained = Boolean(preset.centerWhenContained);

  const clampPosition = (x: number, y: number, scale: number) =>
    clampCropPosition(
      x,
      y,
      scale,
      naturalSize.width,
      naturalSize.height,
      viewportWidth,
      viewportHeight,
      centerWhenContained
    );

  const centerPosition = (scale: number) =>
    clampPosition(
      (viewportWidth - naturalSize.width * scale) / 2,
      (viewportHeight - naturalSize.height * scale) / 2,
      scale
    );

  useEffect(() => {
    if (!open || !imageSrc) {
      setNaturalSize({ width: 0, height: 0 });
      setZoom(IMAGE_CROP_MIN_ZOOM);
      setPosition({ x: 0, y: 0 });
      return;
    }

    const image = new Image();
    image.onload = () => {
      const width = image.naturalWidth;
      const height = image.naturalHeight;
      const scale = getContainScale(
        width,
        height,
        viewportWidth,
        viewportHeight
      );
      const imageMaxZoom = getMaxZoomForImage(
        width,
        height,
        viewportWidth,
        viewportHeight
      );
      setNaturalSize({ width, height });

      // Prefer caller state (JSON / image-diff / cover from useImageCropper).
      // Never override — keeps persisted zoom intact.
      if (initialCropState) {
        const restoredZoom = Math.min(
          imageMaxZoom,
          Math.max(IMAGE_CROP_MIN_ZOOM, initialCropState.zoom)
        );
        const restoredScale = scale * restoredZoom;
        const restoredPosition = clampCropPosition(
          initialCropState.position.x,
          initialCropState.position.y,
          restoredScale,
          width,
          height,
          viewportWidth,
          viewportHeight,
          centerWhenContained
        );
        setZoom(restoredZoom);
        setPosition(restoredPosition);
        return;
      }

      // Safety net when parent passed no state and preset opts into cover.
      // Fresh uploads keep initialCropState null + no defaultFit → contain @ 1.
      if (preset.defaultFit === "cover") {
        const coverState = getCoverAlignedCropViewportState(
          width,
          height,
          viewportWidth,
          viewportHeight
        );
        const coverZoom = Math.min(imageMaxZoom, coverState.zoom);
        const coverScaleEffective = scale * coverZoom;
        const coverPosition = clampCropPosition(
          coverState.position.x,
          coverState.position.y,
          coverScaleEffective,
          width,
          height,
          viewportWidth,
          viewportHeight,
          centerWhenContained
        );
        setZoom(coverZoom);
        setPosition(coverPosition);
        return;
      }

      const contained = clampCropPosition(
        (viewportWidth - width * scale) / 2,
        (viewportHeight - height * scale) / 2,
        scale,
        width,
        height,
        viewportWidth,
        viewportHeight,
        centerWhenContained
      );
      setZoom(IMAGE_CROP_MIN_ZOOM);
      setPosition(contained);
    };
    image.src = imageSrc;
  }, [
    centerWhenContained,
    imageSrc,
    initialCropState,
    open,
    preset.defaultFit,
    viewportHeight,
    viewportWidth,
  ]);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (isBusy) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
    };
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (isBusy || !dragStateRef.current || !naturalSize.width) return;

    const deltaX = (event.clientX - dragStateRef.current.startX) / fitScale;
    const deltaY = (event.clientY - dragStateRef.current.startY) / fitScale;
    const next = clampPosition(
      dragStateRef.current.originX + deltaX,
      dragStateRef.current.originY + deltaY,
      effectiveScale
    );
    setPosition(next);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragStateRef.current = null;
  };

  const isSavingBusy = saving || isSaving;
  const isBusy = isSavingBusy || loading;
  const showViewportLoader = loading || (open && !imageSrc);

  const handleZoomChange = (nextZoom: number) => {
    if (isBusy) return;
    const clampedZoom = Math.min(maxZoom, Math.max(IMAGE_CROP_MIN_ZOOM, nextZoom));
    const nextScale = baseScale * clampedZoom;
    const next = centerPosition(nextScale);
    setZoom(clampedZoom);
    setPosition(next);
  };

  const handleSave = async () => {
    if (isBusy || !imageSrc || !naturalSize.width) return;

    try {
      setIsSaving(true);
      const cropArea = getCropAreaFromViewport(
        position,
        effectiveScale,
        viewportWidth,
        viewportHeight
      );
      const file = await cropImageToFile(
        imageSrc,
        cropArea,
        preset.outputWidth,
        preset.outputHeight,
        `cropped-${Date.now()}.png`
      );
      // Await parent work (upload / DB / query) so Save stays loading.
      await onSave(file, { position, zoom });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Allow cancel while loading original; block only during save/upload.
    if (isSavingBusy) return;
    onCancel();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={modalWidth}
      centered
      destroyOnClose
      className="image-cropper-modal"
      rootClassName="image-cropper-modal-root"
      wrapClassName="image-cropper-modal-wrap"
      title={null}
      focusTriggerAfterClose={false}
      // Portal to body so parent edit modals cannot stretch this dialog to their width.
      getContainer={disableGetContainer ? (() => document.body) : false}
      zIndex={2000}
      closable={!isSavingBusy}
      maskClosable={!isSavingBusy}
      keyboard={!isSavingBusy}
    >
      <div className="image-cropper-modal__header">
        <h3 className="title mb-20">{title}</h3>
        {/* <p className="image-cropper-modal__subtitle">{preset.hint}</p> */}
      </div>

      <div
        className="image-cropper-modal__frame"
        style={{
          width: viewportWidth * fitScale,
          height: viewportHeight * fitScale,
        }}
      >
      <div
        className={`image-cropper-modal__viewport${
          shape === "circle" ? " image-cropper-modal__viewport--circle" : ""
        }${isBusy ? " image-cropper-modal__viewport--busy" : ""}`}
        style={{
          ...viewportStyle,
          width: viewportWidth,
          height: viewportHeight,
          transform: fitScale < 1 ? `scale(${fitScale})` : undefined,
          transformOrigin: "top left",
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {shape !== "circle" && !showViewportLoader ? (
          <div className="image-cropper-modal__grid" aria-hidden />
        ) : null}
        {imageSrc && naturalSize.width > 0 && !showViewportLoader ? (
          <img
            src={imageSrc}
            alt=""
            draggable={false}
            className="image-cropper-modal__image"
            style={{
              width: naturalSize.width * effectiveScale,
              height: naturalSize.height * effectiveScale,
              transform: `translate(${position.x}px, ${position.y}px)`,
            }}
          />
        ) : null}
        {showViewportLoader ? (
          <div className="image-cropper-modal__loading">
            <Spin size="large" />
          </div>
        ) : null}
      </div>
      </div>

      <div
        className={`image-cropper-modal__zoom${
          isBusy ? " image-cropper-modal__zoom--busy" : ""
        }`}
      >
        <span className="image-cropper-modal__zoom-label">ZOOM</span>
        <button
          type="button"
          className="image-cropper-modal__zoom-btn"
          onClick={() => handleZoomChange(zoom - 0.1)}
          aria-label="Zoom out"
          disabled={isBusy}
        >
          <MinusOutlined />
        </button>
        <input
          type="range"
          min={IMAGE_CROP_MIN_ZOOM}
          max={maxZoom}
          step={0.01}
          value={zoom}
          onChange={(event) => handleZoomChange(Number(event.target.value))}
          className="image-cropper-modal__zoom-slider"
          disabled={isBusy}
        />
        <button
          type="button"
          className="image-cropper-modal__zoom-btn"
          onClick={() => handleZoomChange(zoom + 0.1)}
          aria-label="Zoom in"
          disabled={isBusy}
        >
          <PlusOutlined />
        </button>
      </div>

      <div className="image-cropper-modal__footer">
        <button
          type="button"
          className="image-cropper-modal__replace"
          onClick={onReplace}
          disabled={isBusy}
        >
          Replace image
        </button>
        <div className="image-cropper-modal__actions">
          <button
            type="button"
            className="image-cropper-modal__cancel"
            onClick={handleCancel}
            disabled={isSavingBusy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="image-cropper-modal__save"
            onClick={() => void handleSave()}
            disabled={isBusy || !imageSrc}
          >
            {isSavingBusy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ImageCropperModal;
